import { forgotRequest, passwordRequest } from 'core/services/authentication';
import { useEffect, useRef, useState } from 'react';
import PrimaryButton from 'app/components/Button/PrimaryButton';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { validateEmail } from 'utils/validateEmail';
import { IPasswordToken } from 'core/slices/session';
import { SavingState } from 'types/domain/base';
import { LoadingSpinner } from 'app/components/Loading/LoadingSpinner';
import { ISessionReducer } from 'core/slices/session';
import { useSelector } from 'react-redux';
import { selectSession } from 'core/selectors/product/session';
import TextInput from 'app/components/InputField/TextInput';
import { ShieldExclamationIcon } from '@heroicons/react/24/outline';

export enum PasswordType {
  SETUP = 0,
  RESET = 1,
}

export function Password({ type, logo = true }: { type: PasswordType; logo?: boolean }) {
  const dispatch = useAppDispatch();
  const params = useParams();
  const [valid, setValid] = useState(true);
  const [message, setMessage] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const token = useRef(params.token || `${new Date().getTime()}`);
  const passwordToken: IPasswordToken = useAppSelector(state => state.session.passwordTokens[token.current]);
  const navigate = useNavigate();
  const session: ISessionReducer = useSelector(selectSession);

  const enabled = true;
  const passwordMatch = password.length && password2.length && password === password2;

  const error = m => {
    setValid(false);
    setMessage(m);
  };

  const submit = e => {
    if (!validatePassword(password)) {
      error('Password does not meet requirements');
      return;
    }
    if (!passwordMatch) {
      error('Passwords do not match');
      return;
    }

    dispatch(
      passwordRequest({
        token: token.current,
        returnToken: params.returnToken,
        password,
        type,
      }),
    );
  };

  const validPassword = new RegExp(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/);

  const validatePassword = e => {
    return validPassword.test(e);
  };

  useEffect(() => {
    if (passwordToken && passwordToken.saving === SavingState.SAVED && type !== PasswordType.SETUP)
      navigate('/', { replace: false });
  }, [navigate, passwordToken]);

  const getMessage = () => {
    switch (passwordToken.saving) {
      case SavingState.SAVING:
        return <LoadingSpinner />;
      case SavingState.ERROR:
        return <span>Password reset failed</span>;
      case SavingState.SAVED:
        return <span>Please check your email</span>;
    }
  };

  return (
    <div className="mt-12 flex items-center justify-center ">
      <div className="max-w-md w-full space-y-8">
        <div className={'bg-white dark:bg-howio-900 dark:text-white py-8 lg:px-10 px-6 shadow-xl rounded-lg'}>
          {logo && (
            <div className="sm:mx-auto sm:w-full sm:max-w-md mb-5">
              <img
                className="mx-auto h-12 w-auto"
                src={session.config?.logo ? `/${session.config.logo}` : '/howio-logo-wht.svg'}
                alt="Howio Logo"
              />
            </div>
          )}

          <div className=" p-4 mb-5" role="alert">
            Please set a new password, this must be at least eight characters long, contain one number, one uppercase
            and lowercase letter.
          </div>
          <div>
            <>
              <div>
                <label htmlFor="email" className="block text-sm font-medium">
                  Password
                </label>
                <div className="mt-1">
                  <TextInput
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={e => setPassword(e)}
                    required={true}
                    placeholder="Password"
                  />
                </div>
              </div>
              <div className="mt-4 mb-8">
                <label htmlFor="email" className="block text-sm font-medium">
                  Confirm Password
                </label>
                <div className="mt-1">
                  <TextInput
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    value={password2}
                    onChange={e => setPassword2(e)}
                    required={true}
                    placeholder="Confirm Password"
                  />
                </div>
              </div>
            </>
          </div>
          {!passwordToken ? (
            <div className="flex justify-center">
              <PrimaryButton
                className="w-full bg-brand h-12 text-white"
                onClick={submit}
                disabled={!enabled}
                label={'Submit'}
              ></PrimaryButton>
            </div>
          ) : (
            <div className="flex justify-center h-14">{getMessage()}</div>
          )}

          {!valid && (
            <div className="bg-red-600 text-white rounded-md p-4 my-5 flex" role="alert">
              <ShieldExclamationIcon className="h-6 w-6 mr-4"></ShieldExclamationIcon> {message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
